import React, { useCallback, useEffect, useMemo, useState } from "react";
import { adminAnalyticsService } from "../../../../services/adminAnalyticsService";
import SummaryCards from "../SummaryCards";
import DataTable from "../DataTable";
import PaginationControls from "../PaginationControls";
import {
  formatDateTime,
  getFieldValue,
  getPrimaryArray,
  getSecondaryArray,
  getSummaryEntries,
} from "../analyticsUtils";
import { SectionError, SectionLoading } from "../SectionState";

const USER_COLUMNS = [
  {
    key: "email",
    label: "Email",
    render: (_, row) => getFieldValue(row, ["email"]),
  },
  {
    key: "subsection_title",
    label: "Subsection Title",
    render: (_, row) => getFieldValue(row, ["subsection_title", "subsectionTitle"]),
  },
  {
    key: "module_title",
    label: "Module Title",
    render: (_, row) => getFieldValue(row, ["module_title", "moduleTitle"]),
  },
  {
    key: "started_at",
    label: "Started At",
    render: (_, row) => formatDateTime(getFieldValue(row, ["started_at", "startedAt"])),
  },
  {
    key: "days_since_started",
    label: "Days Since Started",
    render: (_, row) => getFieldValue(row, ["days_since_started", "daysSinceStarted"]),
  },
  {
    key: "start_count",
    label: "Start Count",
    render: (_, row) => getFieldValue(row, ["start_count", "startCount"]),
  },
  {
    key: "completion_count",
    label: "Completion Count",
    render: (_, row) => getFieldValue(row, ["completion_count", "completionCount"]),
  },
  {
    key: "restart_count",
    label: "Restart Count",
    render: (_, row) => getFieldValue(row, ["restart_count", "restartCount"]),
  },
];

const StartCompletionReportPanel = ({ paidOnly = false }) => {
  const [query, setQuery] = useState({
    stale_days: 7,
    limit: 200,
    offset: 0,
  });
  const [draft, setDraft] = useState(query);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [selectedCourse, setSelectedCourse] = useState("all");
  const [activeModuleTitle, setActiveModuleTitle] = useState("");

  const loadReport = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const payload = await adminAnalyticsService.getStartCompletionReport({
        ...query,
        paid_only: paidOnly,
      });
      setData(payload);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load start vs completion report.");
    } finally {
      setLoading(false);
    }
  }, [paidOnly, query]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const users = useMemo(
    () =>
      getPrimaryArray(data, [
        "users",
        "started_not_completed_users",
        "stale_users",
        "rows",
      ]),
    [data]
  );
  const sections = useMemo(
    () =>
      getSecondaryArray(
        data,
        ["users", "started_not_completed_users", "stale_users", "rows"],
        ["sections", "section_rows"]
      ),
    [data]
  );
  const summaryEntries = useMemo(() => getSummaryEntries(data), [data]);
  const courseOptions = useMemo(() => {
    return Array.from(
      new Set(
        sections
          .map((row) => getFieldValue(row, ["course_title", "courseTitle"]))
          .filter(Boolean)
      )
    );
  }, [sections]);
  const filteredSections = useMemo(() => {
    if (selectedCourse === "all") return sections;
    return sections.filter(
      (row) =>
        getFieldValue(row, ["course_title", "courseTitle"]) === selectedCourse
    );
  }, [sections, selectedCourse]);
  const groupedSections = useMemo(() => {
    return filteredSections.reduce((acc, row) => {
      const moduleTitle =
        getFieldValue(row, ["module_title", "moduleTitle"]) || "Untitled Module";
      if (!acc[moduleTitle]) acc[moduleTitle] = [];
      acc[moduleTitle].push(row);
      return acc;
    }, {});
  }, [filteredSections]);
  const moduleEntries = useMemo(
    () => Object.entries(groupedSections),
    [groupedSections]
  );
  const activeModuleRows = useMemo(() => {
    if (!moduleEntries.length) return [];
    const activeEntry =
      moduleEntries.find(([moduleTitle]) => moduleTitle === activeModuleTitle) ||
      moduleEntries[0];
    return activeEntry?.[1] || [];
  }, [activeModuleTitle, moduleEntries]);

  useEffect(() => {
    if (!moduleEntries.length) {
      setActiveModuleTitle("");
      return;
    }

    const activeStillExists = moduleEntries.some(
      ([moduleTitle]) => moduleTitle === activeModuleTitle
    );

    if (!activeStillExists) {
      setActiveModuleTitle(moduleEntries[0][0]);
    }
  }, [activeModuleTitle, moduleEntries]);

  const onApply = (event) => {
    event.preventDefault();
    setQuery(draft);
  };

  return (
    <section className="space-y-5 rounded-[32px] border border-slate-200 bg-white p-6 shadow-[0_14px_40px_rgba(15,23,42,0.06)]">
      <div>
        <h3 className="text-2xl font-semibold text-slate-900">Start vs Completion</h3>
        <p className="mt-1 text-sm text-slate-500">
          Track learners who started but did not finish, and review section performance grouped by module.
        </p>
      </div>

      <form
        onSubmit={onApply}
        className="rounded-3xl border border-slate-200 bg-slate-50/70 p-4"
      >
        <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,280px)_1fr_auto] xl:items-end">
        <label className="flex flex-col gap-1 text-sm">
          <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
            Stale Days
          </span>
          <input
            type="number"
            min={1}
            max={180}
            value={draft.stale_days}
            onChange={(e) =>
              setDraft((prev) => ({ ...prev, stale_days: Number(e.target.value) }))
            }
            className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
          />
        </label>

          <PaginationControls
            limit={draft.limit}
            offset={draft.offset}
            onLimitChange={(value) =>
              setDraft((prev) => ({ ...prev, limit: Math.max(1, value || 1) }))
            }
            onOffsetChange={(value) =>
              setDraft((prev) => ({ ...prev, offset: Math.max(0, value || 0) }))
            }
            disabled={loading}
            disableNext={users.length < (draft.limit || 1)}
          />

          <button
            type="submit"
            className="min-w-[160px] rounded-xl bg-[#54B0AF] px-5 py-2.5 text-sm font-medium text-white shadow hover:opacity-90"
            disabled={loading}
          >
            Apply
          </button>
        </div>
      </form>

      {loading && <SectionLoading text="Loading start/completion report..." />}
      {error && <SectionError message={error} />}

      {!loading && !error && (
        <div className="space-y-4">
          <SummaryCards entries={summaryEntries} />
          <DataTable
            title="Users"
            subtitle="Learners with stale subsection progress, shown using the backend user activity fields."
            rows={users}
            columns={USER_COLUMNS}
            emptyText="No users found for current filters."
          />

          <div className="rounded-3xl border border-slate-200 bg-white p-5 shadow-[0_10px_40px_rgba(15,23,42,0.06)]">
            <div className="mb-5 flex flex-wrap items-start justify-between gap-3">
              <div>
                <h4 className="text-lg font-semibold text-slate-900">Sections</h4>
                <p className="mt-1 text-sm text-slate-500">
                  Backend section aggregates grouped under module titles, with a client-side course picker.
                </p>
              </div>

              <label className="flex min-w-[240px] flex-col gap-1 text-sm">
                <span className="text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                  Course
                </span>
                <select
                  value={selectedCourse}
                  onChange={(e) => setSelectedCourse(e.target.value)}
                  className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm text-slate-700"
                >
                  <option value="all">All Courses</option>
                  {courseOptions.map((courseTitle) => (
                    <option key={courseTitle} value={courseTitle}>
                      {courseTitle}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            {!moduleEntries.length ? (
              <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm italic text-slate-500">
                No section data found.
              </div>
            ) : (
              <div className="space-y-4">
                <div className="overflow-x-auto">
                  <div className="flex min-w-max gap-2 border-b border-slate-200">
                    {moduleEntries.map(([moduleTitle, rows]) => {
                      const active = moduleTitle === activeModuleTitle;

                      return (
                        <button
                          key={moduleTitle}
                          type="button"
                          onClick={() => setActiveModuleTitle(moduleTitle)}
                          className={`border-b-2 px-4 py-3 text-left text-sm font-semibold transition ${
                            active
                              ? "border-[#54B0AF] text-slate-900"
                              : "border-transparent text-slate-500 hover:text-slate-900"
                          }`}
                        >
                          <span className="block whitespace-nowrap">
                            {moduleTitle}
                          </span>
                          <span className="mt-1 block text-xs font-medium text-slate-400">
                            {rows.length} sections
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                <div className="overflow-hidden rounded-2xl border border-slate-200">
                  <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
                    <div className="text-sm font-semibold text-slate-900">
                      {activeModuleTitle || moduleEntries[0]?.[0]}
                    </div>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="min-w-full border-separate border-spacing-0 text-sm">
                      <thead>
                        <tr>
                          <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                            Subsection Title
                          </th>
                          <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                            Started Users
                          </th>
                          <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                            Completed Users
                          </th>
                          <th className="border-b border-slate-200 px-4 py-3 text-left text-xs font-semibold uppercase tracking-[0.12em] text-slate-500">
                            Completion Rate Pct
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {activeModuleRows.map((row, index) => (
                          <tr key={`${activeModuleTitle}-${index}`} className="transition-colors hover:bg-slate-50/70">
                            <td className="border-b border-slate-100 px-4 py-3 text-slate-700">
                              {getFieldValue(row, ["subsection_title", "subsectionTitle"]) || "--"}
                            </td>
                            <td className="border-b border-slate-100 px-4 py-3 text-slate-700">
                              {getFieldValue(row, ["started_users", "startedUsers"]) ?? "--"}
                            </td>
                            <td className="border-b border-slate-100 px-4 py-3 text-slate-700">
                              {getFieldValue(row, ["completed_users", "completedUsers"]) ?? "--"}
                            </td>
                            <td className="border-b border-slate-100 px-4 py-3 text-slate-700">
                              {getFieldValue(row, ["completion_rate_pct", "completionRatePct"]) ?? "--"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </section>
  );
};

export default StartCompletionReportPanel;

